import type { RouteLocationNormalized } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';

/**
 * Tanstack query to load a ESAV booking by its bookingId from the route.
 * - Loads booking and returns all the transformed data (tickets, refundables, productInfos, etc)
 * - Also returns computed properties to check if allowed to download tickets, refund or exchange
 */
export default function useLoadEsavBooking(routeParam?: RouteLocationNormalized) {
    const route = computed(() => routeParam ?? useRoute<'esav'>());
    const bookingId = computed(() => toValue(route.value.params.bookingId as string));
    const query = getQuery(bookingId);

    const booking = computed(() => query.data.value?.data.booking);
    const productInfos = computed(() => query.data.value?.data.productInfos ?? []);
    const ticketGroups = computed(() => query.data.value?.data.ticketGroups ?? []);
    const refundableGroups = computed(() => query.data.value?.data.refundableGroups ?? []);
    const exchangeableGroups = computed(() => query.data.value?.data.exchangeableGroups ?? []);

    /**
     *
     * Does the booking contain any valid ticket downloads?
     *
     */
    const canDownloadTickets = computed(() => ticketGroups.value
        ?.flatMap(tg => tg.tickets as Ticket[])
        .some(t => t.isDownloadable));

    /**
     *
     * Does the booking contain any items eligible for refund?
     */
    const canRefund = computed(() => refundableGroups.value
        ?.flatMap(r => r.refundables as Refundable[])
        .some(r => r.isRefundable));

    /**
     *
     * Does the booking contain any items eligible for exchange?
     */
    const canExchange = computed(() => exchangeableGroups.value
        ?.flatMap(r => r.exchangeables as Exchangeable[])
        .some(r => r.isExchangeable));

    return {
        ...query,
        booking,
        productInfos,
        ticketGroups,
        refundableGroups,
        exchangeableGroups,
        canDownloadTickets,
        canRefund,
        canExchange,
    };
}

function getQuery(bookingId: Ref<string>) {
    return useQuery({
        queryKey: [
            'esav-booking',
            bookingId,
        ],
        queryFn: () => apiLoadEsavBooking(bookingId.value),
        enabled: () => !!bookingId.value,
        throwOnError: true,
        staleTime: 1000 * 60 * 0, // Always reload
        refetchOnWindowFocus: false,
    });
}
